import { systemMessage } from '../../../utils/localizedSystemMessages';
import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import ProfilePhoto, { getProfilePhoto } from '../../ProfilePhoto';
import { ref, update, push } from 'firebase/database';
import { database } from '../../../firebase';
import { ActionButton } from './QuickActions';
import ActivityTimeline from './ActivityTimeline';
import NotesSection from './NotesSection';
import DocumentManager from './DocumentManager';
import { field, sectionTitle } from './EmployeeDrawerSections';
import EmailComposer from './modals/EmailComposer';
import ScheduleReviewModal from './modals/ScheduleReviewModal';
import ApprovePTOModal from './modals/ApprovePTOModal';
import EditCompensationModal from './modals/EditCompensationModal';
import UploadDocumentModal from './modals/UploadDocumentModal';
import AddBonusModal from './modals/AddBonusModal';
import OffboardModal from './modals/OffboardModal';
import ReviewRequestsModal from './modals/ReviewRequestsModal';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../../contexts/LanguageContext';
import { translateDepartment } from '../../../utils/translationUtils';
import { useNavigate } from 'react-router-dom';
import { invalidateAnalyticsSessionCache } from '../../../services/atsAnalyticsService';
import nationalizationService, {
  addNationalizationLifecycleEventToUpdates,
  announceNationalizationWorkforceChange
} from '../../../services/nationalizationService';
import '../MyTeamWorkspace.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL_PROD || 'http://localhost:8000';

const EmployeePage = ({
  employeeId,
  employeesRaw,
  onClose,
  onBack,
  drawerOpen,
  fullPage = false,
  companyUID: companyUIDProp
}) => {
  useUiLanguage();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { t } = useTranslation(['myteam', 'common']);
  const { isRTL, language } = useLanguage();
  const [activities, setActivities] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const notesSectionRef = useRef(null);
  const [activeModal, setActiveModal] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [mobilitySaving, setMobilitySaving] = useState(false);
  const [growthSettingsSaving, setGrowthSettingsSaving] = useState(false);
  const [qualitySaving, setQualitySaving] = useState(false);
  const [qualityForm, setQualityForm] = useState({});
  const [pathwayGenerating, setPathwayGenerating] = useState(false);
  const [pathwayError, setPathwayError] = useState('');

  const emp = employeesRaw[employeeId] || {};
  const profile = emp.profile || {};
  const contact = emp.contact || {};
  const lifecycle = emp.lifecycle || {};
  const pto = emp.pto || {};
  const compensation = emp.compensation || {};
  const performance = emp.performance || {};
  const qualityOfHire = emp.qualityOfHire || {};
  const documents = emp.documents || {};
  const meta = emp.meta || {};
  const portal = emp.portal || {};
  const address = contact.address || {};
  const emergencies = Array.isArray(contact.emergencyContacts) ? contact.emergencyContacts : Object.values(contact.emergencyContacts || {});
  const location = profile.location || {};
  const probation = profile.probation || {};
  const departmentLabel = translateDepartment(profile.department || emp.department, language);
  const mobility = emp.mobility || {};
  const profilePhotoUrl = getProfilePhoto(profile, emp);
  const performanceReviews = Object.entries(performance.reviews || {});
  const performanceGoals = Object.entries(performance.goals || {});
  const growthSettings = performance.growthSettings || {};
  const learningPathway = emp.learningPathway || {};
  const documentsList = Object.entries(documents.files || {}).map(([id, doc]) => ({ id, ...doc }));
  const hasUploadedCv = documentsList.some((doc) => {
    const type = String(doc.type || '').toLowerCase();
    const name = String(doc.name || '').toLowerCase();
    return type === 'resume' || type === 'cv' || name.includes('resume') || name.includes('cv');
  });

  const companyUID = companyUIDProp || meta.companyUID || '';
  const locale = language === 'ar' ? 'ar-LB' : 'en-US';
  const statusLabels = useMemo(() => ({
    active: t('status.active', 'Active'),
    offboarded: t('status.offboarded', 'Offboarded'),
    onLeave: t('status.onLeave', 'On Leave / PTO'),
    probation: t('status.probation', 'On Probation'),
    onboarding: t('status.onboarding', 'Onboarding')
  }), [t]);
  const employmentTypeLabel = (val) => {
    const clean = (val || 'full_time').toLowerCase();
    return t(`employmentTypes.${clean}`, (clean || '').replace('_', ' '));
  };
  const allowanceLabel = t('drawerModals.compInline.allowance', 'Allowance');
  const deductionLabel = t('drawerModals.compInline.deduction', 'Deduction');

  // Payroll Logic
  const lastPaid = compensation.lastPaidAt ? new Date(compensation.lastPaidAt) : null;
  const now = new Date();
  const isPaidThisMonth = lastPaid &&
    lastPaid.getMonth() === now.getMonth() &&
    lastPaid.getFullYear() === now.getFullYear();

  // Calculate monthly total
  const baseSalary = Number(compensation.base || 0);
  const allowanceTotal = (compensation.allowances || []).reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);
  const deductionTotal = (compensation.deductions || []).reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);

  // Bonuses for this month
  const currentBonuses = Object.values(compensation.bonuses || {}).filter(b => {
    const d = new Date(b.date);
    return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  });
  const bonusTotal = currentBonuses.reduce((acc, curr) => acc + (Number(curr.amount) || 0), 0);

  const monthlyTotal = baseSalary + allowanceTotal + bonusTotal - deductionTotal;

  const selectedCourseCount = (learningPathway.steps || []).filter((step) => step.selectedCourseId).length;
  const pathwayTarget = learningPathway.target || {};
  const pathwayStatus = pathwayGenerating ? 'generating' : (learningPathway.status || 'idle');
  const employeeReady = Boolean(employeeId && Object.prototype.hasOwnProperty.call(employeesRaw || {}, employeeId));

  const handleGenerateLearningPathway = async () => {
    if (!companyUID || !employeeId || !currentUser?.uid) return;
    setPathwayGenerating(true);
    setPathwayError('');
    try {
      const response = await fetch(`${BACKEND_URL}/employee/learning-pathway/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyUID,
          employeeId,
          userUID: currentUser.uid,
          userEmail: currentUser.email || '',
          generatedBy: currentUser.email || currentUser.uid
        })
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to generate learning pathway');
      }
      fetchActivities();
    } catch (error) {
      console.error('Learning pathway generation failed:', error);
      setPathwayError(error.message || 'Failed to generate learning pathway');
    } finally {
      setPathwayGenerating(false);
    }
  };

  const openLearningPathway = () => {
    if (!employeeId) return;
    navigate(`/my-team/employee/${employeeId}/learning-pathway`);
  };

  const handleMarkAsPaid = async () => {
    const confirmLabel = t('drawerModals.payroll.confirmMarkPaid', {
      name: profile.name || emp.name,
      defaultValue: `Mark payroll as PAID for ${profile.name || emp.name} for this month?`
    });
    if (window.confirm(confirmLabel)) {
      try {
        await update(ref(database, `companies/${companyUID}/employees/${employeeId}/compensation`), {
          status: 'paid',
          lastPaidAt: Date.now()
        });
        // This is internal payroll bookkeeping only; it is not official WPS or
        // government payment evidence. Regulatory WPS evidence is changed only
        // through the protected, sourced nationalization import flow.
        // Log activity
        await push(ref(database, `companies/${companyUID}/employees/${employeeId}/activities`), {
          type: 'payroll_paid',
          description: 'Payroll marked as paid',
          timestamp: Date.now(),
          by: currentUser?.email || 'Admin'
        });
        fetchActivities();
      } catch (e) {
        console.error(e);
        alert(t('drawerModals.payroll.updateFailed', 'Failed to update status'));
      }
    }
  };

  const handleUpdateCompList = async (key, newList) => {
    try {
      const source = `employee_compensation_${key}_change`;
      const workforceUpdates = {
        [`companies/${companyUID}/employees/${employeeId}/compensation/${key}`]: newList
      };
      // Allowances and deductions can be part of a country's legal wage formula.
      // Commit the edit and minimized invalidation marker together; values never
      // enter the marker itself.
      const lifecycleEvent = addNationalizationLifecycleEventToUpdates(workforceUpdates, {
        companyUID,
        employeeId,
        source,
        changeKinds: ['compensation']
      });
      await update(ref(database), workforceUpdates);
      let lifecycleSync = null;
      try {
        lifecycleSync = await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
          eventId: lifecycleEvent.eventId,
          source,
          changeKinds: ['compensation']
        });
      } catch (syncError) {
        console.warn('Nationalization compensation review is pending:', syncError);
      }
      announceNationalizationWorkforceChange({
        companyUID,
        source,
        recalculated: false,
        syncPending: !lifecycleSync
      });
    } catch (e) {
      console.error(e);
      alert(t('drawerModals.compInline.updateListFailed', { key, defaultValue: `Failed to update ${key}` }));
    }
  };

  const handleAddCompItem = (type) => {
    const label = type === 'Allowance' ? allowanceLabel : deductionLabel;
    const name = prompt(t('drawerModals.compInline.namePrompt', { type: label, defaultValue: `Enter name for new ${label}:` }));
    if (!name) return;
    const amountStr = prompt(t('drawerModals.compInline.amountPrompt', { name, defaultValue: `Enter amount for ${name}:` }));
    const amount = parseFloat(amountStr);
    if (isNaN(amount)) { alert(t('drawerModals.compInline.invalidAmount', 'Invalid amount')); return; }

    const key = type === 'Allowance' ? 'allowances' : 'deductions';
    const currentList = compensation[key] || [];
    const newItem = { name, amount, type: type.toLowerCase() };
    handleUpdateCompList(key, [...currentList, newItem]);
  };

  const handleRemoveCompItem = (type, index) => {
    if (!window.confirm(t('drawerModals.compInline.removeConfirm', 'Remove this item?'))) return;
    const key = type === 'Allowance' ? 'allowances' : 'deductions';
    const currentList = compensation[key] || [];
    const newList = currentList.filter((_, i) => i !== index);
    handleUpdateCompList(key, newList);
  };

  const toggleManagerRole = async () => {
    if (!companyUID || !employeeId) return;

    const currentPermissions = portal.permissions || ['employee'];
    let newPermissions;

    if (currentPermissions.includes('manager')) {
      newPermissions = currentPermissions.filter(p => p !== 'manager');
    } else {
      newPermissions = [...currentPermissions, 'manager'];
    }

    try {
      const updates = {};
      updates[`companies/${companyUID}/employees/${employeeId}/portal/permissions`] = newPermissions;
      await update(ref(database), updates);
    } catch (error) {
      console.error("Error updating permissions:", error);
      alert(t('drawerModals.compInline.permissionsFailed', 'Failed to update permissions'));
    }
  };

  // Fetch activities and notes when drawer opens
  useEffect(() => {
    if (employeeId && companyUID) {
      fetchActivities();
      fetchNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, companyUID]);

  useEffect(() => {
    setQualityForm({
      betaScore: qualityOfHire.betaScore ?? '',
      rampStatus: qualityOfHire.rampStatus || 'not_started',
      rampDate: qualityOfHire.rampDate || '',
      managerRating: qualityOfHire.managerRating ?? '',
      notes: qualityOfHire.notes || '',
      probationStatus: probation.status || (probation.inProbation ? 'in_progress' : 'not_applicable'),
      probationOutcomeDate: probation.outcomeDate || '',
      lifecycleStatus: lifecycle.status || emp.status || 'active',
      terminationDate: lifecycle.terminationDate || '',
      terminationReason: lifecycle.terminationReason || '',
      terminationType: lifecycle.terminationType || 'unknown'
    });
  }, [
    employeeId,
    emp.status,
    lifecycle.status,
    lifecycle.terminationDate,
    lifecycle.terminationReason,
    lifecycle.terminationType,
    probation.status,
    probation.inProbation,
    probation.outcomeDate,
    qualityOfHire.betaScore,
    qualityOfHire.rampStatus,
    qualityOfHire.rampDate,
    qualityOfHire.managerRating,
    qualityOfHire.notes
  ]);

  const fetchActivities = async () => {
    setLoadingActivities(true);
    try {
      if (!currentUser) throw new Error('Authentication required');
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${BACKEND_URL}/employee/activities?companyUID=${companyUID}&employeeId=${employeeId}&limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      console.log('Activities API Response:', data);
      if (data.success && data.activities) {
        // Convert Firebase object to array if needed
        let activitiesArray = Array.isArray(data.activities) ? data.activities : Object.entries(data.activities || {}).map(([id, activity]) => ({
          id,
          ...activity
        }));
        // Activities are already sorted descending by backend, which is correct for timeline
        console.log('Processed activities:', activitiesArray);
        setActivities(activitiesArray);
      } else {
        console.log('No activities found or API error:', data);
        setActivities([]);
      }
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const fetchNotes = async () => {
    setLoadingNotes(true);
    try {
      if (!currentUser) throw new Error('Authentication required');
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${BACKEND_URL}/employee/notes?companyUID=${companyUID}&employeeId=${employeeId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await response.json();
      console.log('Notes API Response:', data);
      if (data.success && data.notes) {
        // Convert Firebase object to array if needed
        let notesArray = Array.isArray(data.notes) ? data.notes : Object.entries(data.notes || {}).map(([id, note]) => ({
          id,
          ...note
        }));
        // Sort notes by timestamp ascending (oldest first) for display
        const sortedNotes = [...notesArray].sort((a, b) => {
          const tsA = a.timestamp || 0;
          const tsB = b.timestamp || 0;
          return tsA - tsB; // Ascending order
        });
        console.log('Processed notes:', sortedNotes);
        setNotes(sortedNotes);
      } else {
        console.log('No notes found or API error:', data);
        setNotes([]);
      }
    } catch (error) {
      console.error('Failed to fetch notes:', error);
      setNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleQuickAction = (action) => {
    console.log('Quick action:', action, 'for employee:', employeeId);
    setActiveModal(action);
  };

  const handleModalClose = () => {
    setActiveModal(null);
  };

  const handleModalSuccess = () => {
    // Refresh data after successful action
    fetchActivities();
    fetchNotes();
    // Note: Employee data will refresh automatically via Firebase listeners
  };

  const pendingPTOCount = Object.values(pto.requests || {}).filter(req => req.status === 'pending').length;

  // Count pending HR requests (manager-initiated requests needing HR approval)
  const requests = emp.requests || {};
  const pendingHRRequests = Object.entries(requests)
    .map(([key, val]) => ({ id: key, ...val }))
    .filter(r => r.status === 'pending_hr');
  const pendingRequestsCount = pendingHRRequests.length;

  // Status determination
  const lifecycleStatus = (lifecycle.status || emp.status || '').toLowerCase();
  let employeeStatus = statusLabels.active;
  let statusColor = '#40EB6E';

  if (lifecycleStatus === 'offboarded' || lifecycleStatus === 'terminated') {
    employeeStatus = statusLabels.offboarded;
    statusColor = '#EB4040';
  } else if (lifecycleStatus === 'on_leave' || lifecycleStatus === 'leave') {
    employeeStatus = statusLabels.onLeave;
    statusColor = '#CC40EB';
  } else if (probation.inProbation) {
    employeeStatus = statusLabels.probation;
    statusColor = '#EB8D40';
  } else if (lifecycleStatus === 'onboarding') {
    employeeStatus = statusLabels.onboarding;
    statusColor = '#EBDD40';
  }

  const panelBaseStyle = fullPage ? null : {
    position: 'fixed',
    top: 0,
    [isRTL ? 'left' : 'right']: drawerOpen ? 0 : '-50%',
    width: '50%',
    height: '100vh',
    backgroundColor: 'var(--app-background, #031128)',
    zIndex: 2000,
    transition: `${isRTL ? 'left' : 'right'} 250ms ease`,
    willChange: 'transform',
  };

  const panelContentStyle = fullPage ? {
    position: 'relative',
    minHeight: 'calc(100vh - 100px)',
    width: '100%',
    backgroundColor: 'var(--app-background, #031128)',
    overflowY: 'auto',
    padding: '0 0 40px',
    boxSizing: 'border-box',
    direction: isRTL ? 'rtl' : 'ltr'
  } : {
    position: 'fixed',
    top: 0,
    [isRTL ? 'left' : 'right']: drawerOpen ? 0 : '-50%',
    width: '50%',
    height: '100vh',
    backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.05))',
    borderLeft: "1px solid var(--app-border, rgba(86, 141, 237, 0.10))",
    zIndex: 2001,
    transition: `${isRTL ? 'left' : 'right'} 250ms ease`,
    overflowY: 'auto',
    padding: '40px',
    boxSizing: 'border-box',
    willChange: 'transform',
    direction: isRTL ? 'rtl' : 'ltr'
  };

  const overlayActionButtonStyle = {
    position: 'absolute',
    top: 20,
    width: fullPage ? 'auto' : 40,
    height: 40,
    padding: fullPage ? '0 14px' : 0,
    [fullPage ? 'insetInlineStart' : 'insetInlineEnd']: 20,
    backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.1))',
    border: "1px solid var(--app-border, rgba(86, 141, 237, 0.2))",
    borderRadius: '10px',
    color: 'var(--app-text, #FFFFFF)',
    fontSize: fullPage ? '14px' : '20px',
    fontFamily: 'Montserrat',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    transition: 'all 0.2s ease',
    zIndex: 2
  };

  const fullPageBackButtonStyle = {
    position: 'absolute',
    top: 20,
    insetInlineStart: 20,
    background: 'transparent',
    color: '#568ded',
    border: '1.5px solid #568ded',
    borderRadius: 20,
    padding: '8px 16px',
    cursor: 'pointer',
    fontFamily: 'Montserrat, sans-serif',
    fontWeight: 600,
    marginTop: 0,
    marginBottom: 20,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
    transition: 'all 0.2s ease',
    zIndex: 2
  };

  const TAB_OPTIONS = [
    { id: 'overview', label: t('employeePage.tabs.profile', 'Profile') },
    { id: 'work', label: t('employeePage.tabs.performanceGrowth', 'Performance & Growth') },
    { id: 'pay', label: t('employeePage.tabs.payLeave', 'Pay & Leave') },
    { id: 'documents', label: t('employeePage.tabs.files', 'Files') },
    { id: 'activity', label: t('employeePage.tabs.activity', 'Activity') }
  ];

  const mobilityReadiness = mobility.readiness || (
    performance.currentRating >= 4 ? 'ready_now' :
    performance.currentRating >= 3 ? 'developing' :
    'not_ready'
  );

  const handleSaveMobility = async (form) => {
    if (!companyUID || !employeeId) return;
    const nextDepartment = (form.targetDepartment || '').trim();
    const nextRole = (form.targetRole || '').trim();
    if (!nextDepartment || !nextRole) {
      alert(t('employeePage.mobility.validation', 'Please enter both the target role and target department.'));
      return;
    }

    setMobilitySaving(true);
    try {
      const updates = {};
      updates[`companies/${companyUID}/employees/${employeeId}/department`] = nextDepartment;
      updates[`companies/${companyUID}/employees/${employeeId}/role`] = nextRole;
      updates[`companies/${companyUID}/employees/${employeeId}/roleDepartment`] = [nextRole, nextDepartment].filter(Boolean).join(' / ');
      updates[`companies/${companyUID}/employees/${employeeId}/profile/department`] = nextDepartment;
      updates[`companies/${companyUID}/employees/${employeeId}/profile/roleTitle`] = nextRole;
      updates[`companies/${companyUID}/employees/${employeeId}/mobility`] = {
        active: true,
        targetDepartment: nextDepartment,
        targetRole: nextRole,
        readiness: form.readiness || 'developing',
        effectiveDate: form.effectiveDate || '',
        notes: form.notes || '',
        updatedAt: Date.now(),
        updatedBy: currentUser?.email || 'Admin'
      };
      // A role/department move makes the prior regulatory occupation mapping
      // unsafe. Queue server-side invalidation without copying the new title into
      // protected legal facts; a manager must review and confirm the new mapping.
      const lifecycleEvent = addNationalizationLifecycleEventToUpdates(updates, {
        companyUID,
        employeeId,
        source: 'employee_internal_mobility',
        changeKinds: ['occupation']
      });
      await update(ref(database), updates);
      try {
        await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
          eventId: lifecycleEvent.eventId,
          source: 'employee_internal_mobility',
          changeKinds: ['occupation']
        });
      } catch (syncError) {
        console.warn('Nationalization occupation review is pending:', syncError);
      }
      announceNationalizationWorkforceChange({
        companyUID,
        source: 'employee_internal_mobility',
        recalculated: false
      });
      await push(ref(database, `companies/${companyUID}/employees/${employeeId}/activities`), {
        type: 'internal_mobility',
        ...systemMessage('internal_mobility', { role: nextRole, department: nextDepartment }),
        description: `Internal mobility updated to ${nextRole} / ${nextDepartment}`,
        timestamp: Date.now(),
        by: currentUser?.email || 'Admin'
      });
      setActiveModal(null);
      fetchActivities();
    } catch (error) {
      console.error('Failed to update internal mobility:', error);
      alert(t('employeePage.mobility.failure', 'Failed to update internal mobility.'));
    } finally {
      setMobilitySaving(false);
    }
  };

  const handleSaveGrowthSettings = async (nextSettings) => {
    if (!companyUID || !employeeId) return;

    setGrowthSettingsSaving(true);
    try {
      await update(ref(database, `companies/${companyUID}/employees/${employeeId}/performance`), {
        growthSettings: {
          ...growthSettings,
          ...nextSettings,
          updatedAt: Date.now(),
          updatedBy: currentUser?.email || 'Admin'
        }
      });
      await push(ref(database, `companies/${companyUID}/employees/${employeeId}/activities`), {
        type: 'performance_framework_updated',
        description: 'Performance and growth framework updated',
        timestamp: Date.now(),
        by: currentUser?.email || 'Admin'
      });
      fetchActivities();
    } catch (error) {
      console.error('Failed to save growth settings:', error);
      alert(t('employeePage.performance.saveFailed', 'Failed to save performance and growth settings.'));
    } finally {
      setGrowthSettingsSaving(false);
    }
  };

  const updateQualityForm = (fieldName, value) => {
    setQualityForm((current) => ({ ...current, [fieldName]: value }));
  };

  const handleSaveQualityBeta = async () => {
    if (!companyUID || !employeeId) return;
    const hasBetaScore = qualityForm.betaScore !== '' && qualityForm.betaScore !== null && qualityForm.betaScore !== undefined;
    const hasManagerRating = qualityForm.managerRating !== '' && qualityForm.managerRating !== null && qualityForm.managerRating !== undefined;
    const betaScore = hasBetaScore ? Math.max(0, Math.min(Number(qualityForm.betaScore), 100)) : null;
    const managerRating = hasManagerRating ? Math.max(0, Math.min(Number(qualityForm.managerRating), 5)) : null;

    if ((hasBetaScore && Number.isNaN(betaScore)) || (hasManagerRating && Number.isNaN(managerRating))) {
      alert(t('employeePage.qualityBeta.invalidNumbers', 'Quality score and manager rating must be valid numbers.'));
      return;
    }

    const nextProbationStatus = qualityForm.probationStatus || 'not_applicable';
    const nextLifecycleStatus = qualityForm.lifecycleStatus || 'active';
    const updatedBy = currentUser?.email || currentUser?.uid || 'Admin';
    setQualitySaving(true);
    try {
      const updates = {};
      updates[`companies/${companyUID}/employees/${employeeId}/qualityOfHire`] = {
        ...(qualityOfHire || {}),
        betaScore,
        rampStatus: qualityForm.rampStatus || 'not_started',
        rampDate: qualityForm.rampDate || '',
        managerRating,
        notes: qualityForm.notes || '',
        updatedAt: Date.now(),
        updatedBy
      };
      updates[`companies/${companyUID}/employees/${employeeId}/profile/probation`] = {
        ...(probation || {}),
        status: nextProbationStatus,
        inProbation: nextProbationStatus === 'in_progress',
        outcomeDate: qualityForm.probationOutcomeDate || ''
      };
      updates[`companies/${companyUID}/employees/${employeeId}/lifecycle/status`] = nextLifecycleStatus;
      updates[`companies/${companyUID}/employees/${employeeId}/lifecycle/terminationDate`] = qualityForm.terminationDate || '';
      updates[`companies/${companyUID}/employees/${employeeId}/lifecycle/terminationReason`] = qualityForm.terminationReason || '';
      updates[`companies/${companyUID}/employees/${employeeId}/lifecycle/terminationType`] = qualityForm.terminationType || 'unknown';
      updates[`companies/${companyUID}/employees/${employeeId}/lifecycle/lastActivityAt`] = Date.now();
      updates[`companies/${companyUID}/employees/${employeeId}/status`] = nextLifecycleStatus;

      const lifecycleEvent = addNationalizationLifecycleEventToUpdates(updates, {
        companyUID,
        employeeId,
        source: 'employee_lifecycle_edit',
        changeKinds: ['lifecycle']
      });

      await update(ref(database), updates);
      let lifecycleSync = null;
      try {
        lifecycleSync = await nationalizationService.syncEmployeeLifecycle(companyUID, employeeId, {
          eventId: lifecycleEvent.eventId,
          source: 'employee_lifecycle_edit',
          changeKinds: ['lifecycle']
        });
      } catch (syncError) {
        console.warn('Nationalization lifecycle sync is pending:', syncError);
      }
      announceNationalizationWorkforceChange({
        companyUID,
        source: 'employee_lifecycle_edit',
        recalculated: false,
        syncPending: !lifecycleSync
      });
      await push(ref(database, `companies/${companyUID}/employees/${employeeId}/activities`), {
        type: 'quality_of_hire_beta_updated',
        description: 'Quality of Hire Beta fields updated',
        timestamp: Date.now(),
        by: updatedBy
      });
      invalidateAnalyticsSessionCache(companyUID);
      fetchActivities();
    } catch (error) {
      console.error('Failed to save Quality of Hire Beta fields:', error);
      alert(t('employeePage.qualityBeta.saveFailed', 'Failed to save Quality of Hire Beta fields.'));
    } finally {
      setQualitySaving(false);
    }
  };

  const quickOverviewActions = (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
      {handleMarkAsPaid && !isPaidThisMonth && (
        <ActionButton
          icon="fa-file-invoice-dollar"
          label={t('drawerQuickActions.markPaid', 'Mark as Paid')}
          onClick={handleMarkAsPaid}
        />
      )}
      <ActionButton icon="fa-envelope" label={t('drawerQuickActions.sendEmail', 'Send Email')} onClick={() => handleQuickAction('email')} />
      <ActionButton icon="fa-check-circle" label={t('drawerQuickActions.approvePto', 'Approve PTO')} onClick={() => handleQuickAction('approve-pto')} count={pendingPTOCount} />
      <ActionButton icon="fa-upload" label={t('drawerQuickActions.upload', 'Upload Document')} onClick={() => handleQuickAction('upload-doc')} />
    </div>
  );

  if (!employeeReady) {
    return (
      <>
        {!fullPage && <div style={panelBaseStyle} />}
        <div style={panelContentStyle}>
          <div className="data-analytics-loading-screen">
            <i className="fa-solid fa-spinner fa-spin" />
            <p>{t('common:status.pleaseWait', 'Please Wait')}</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {!fullPage && <div style={panelBaseStyle} />}

      {/* Content layer */}
      <div style={panelContentStyle}>
        {fullPage ? (
          <button
            onClick={onBack || onClose}
            style={fullPageBackButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'var(--app-surface, rgba(86, 141, 237, 0.1))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <i className={`fa-solid ${isRTL ? 'fa-arrow-right' : 'fa-arrow-left'}`}></i>
            <span>{t('employeePage.back', 'Back to My Team')}</span>
          </button>
        ) : (
          <button
            onClick={onClose}
            style={overlayActionButtonStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--app-surface, rgba(86, 141, 237, 0.2))';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--app-surface, rgba(86, 141, 237, 0.1))';
            }}
          >
            ×
          </button>
        )}

        <button className="post-job-cta secondary" style={{ alignSelf: 'flex-end' }} onClick={() => navigate(`/payroll?employee=${encodeURIComponent(employeeId)}`)}><i className="fa-solid fa-money-check-dollar" aria-hidden="true" /><span>{uiText('Payroll settings')}</span></button>
        {/* Profile Section */}
        <div style={{ marginBottom: 28, paddingTop: fullPage ? 80 : 0, paddingInline: fullPage ? 40 : 0 }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: 24,
            marginBottom: 24
          }}>
            <div style={{ position: 'relative' }}>
              {/* Decorative frame */}
              <div style={{
                position: 'absolute',
                top: -8,
                left: -8,
                right: -8,
                bottom: -8,
                border: "2px solid var(--app-border-strong, rgba(86, 141, 237, 0.3))",
                borderRadius: '16px',
                transform: 'rotate(-2deg)',
              }} />
              <div style={{
                position: 'absolute',
                top: -4,
                left: -4,
                right: -4,
                bottom: -4,
                border: '1px solid rgba(86, 141, 237, 0.5)',
                borderRadius: '14px',
                transform: 'rotate(1deg)',
              }} />
              {/* Profile picture */}
              <div style={{
                position: 'relative',
                width: 140,
                height: 140,
                borderRadius: '12px',
                overflow: 'hidden',
                border: "3px solid var(--app-border, rgba(86, 141, 237, 0.2))",
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              }}>
                <ProfilePhoto
                  src={profilePhotoUrl}
                  alt={profile.name || emp.name || uiText("Employee")}
                />
              </div>
            </div>

            {/* Name and basic info */}
            <div style={{ flex: 1 }}>
              <h2 style={{
                margin: 0,
                marginBottom: 8,
                fontFamily: 'Montserrat',
                fontSize: '28px',
                fontWeight: "var(--app-weight-strong, 700)",
                color: 'var(--app-text, #FFFFFF)',
              }}>
                {profile.name || emp.name || '—'}
              </h2>
              <div style={{
                fontFamily: 'Montserrat',
                fontSize: '16px',
                color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.8)',
                marginBottom: 12
              }}>
                {profile.roleTitle || emp.role || '—'}
                {departmentLabel && (
                  <span style={{ opacity: 0.6 }}> • {departmentLabel}</span>
                )}
              </div>
              {/* Status badge */}
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 12px',
                backgroundColor: `${statusColor}20`,
                border: `1px solid ${statusColor}40`,
                borderRadius: '20px',
              }}>
                <span style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: statusColor,
                }} />
                <span style={{
                  fontFamily: 'Montserrat',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: 'var(--app-text, #FFFFFF)',
                }}>
                  {employeeStatus}
                </span>
              </div>
              {/* Employee ID */}
              {(meta.employeeNumber || emp.employeeId) && (
                <div style={{
                  marginTop: 12,
                  fontFamily: 'Montserrat',
                  fontSize: '13px',
                  color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)',
                }}>{uiText("ID: ")}{meta.employeeNumber || emp.employeeId}
                </div>
              )}
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 320 }}>
            {quickOverviewActions}
          </div>
        </div>

        <div style={{ paddingInline: fullPage ? 40 : 0 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 28 }}>
          {TAB_OPTIONS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                padding: '10px 16px',
                borderRadius: 999,
                border: `1px solid ${activeTab === tab.id ? 'rgba(86,141,237,0.45)' : 'var(--app-border, rgba(255,255,255,0.1))'}`,
                backgroundColor: activeTab === tab.id ? 'var(--app-surface, rgba(86,141,237,0.16))' : 'var(--app-surface, rgba(255,255,255,0.03))',
                color: 'var(--app-text, #FFFFFF)',
                fontFamily: 'Montserrat',
                fontSize: 13,
                fontWeight: 600,
                boxShadow: activeTab === tab.id ? 'inset 0 0 0 1px rgba(86,141,237,0.12)' : 'none',
                cursor: 'pointer'
              }}
              data-light-tab={activeTab === tab.id}
              data-light-control={activeTab === tab.id ? "quiet" : "quiet"}>
              {uiText(tab.label)}
            </button>
          ))}
        </div>

        {activeTab === 'overview' && (
          <>
            {(lifecycle.alerts?.payrollUnpaid || !isPaidThisMonth || (lifecycle.alerts?.docsMissing > 0) || lifecycle.alerts?.reviewDue || lifecycle.alerts?.leavePending) && (
              <>
                {sectionTitle('fa-solid fa-bell', t('drawerSections.alerts', 'Active Alerts'))}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                  {(lifecycle.alerts?.payrollUnpaid || !isPaidThisMonth) && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', backgroundColor: '#EB40404D', border: '1px solid #EB404026', borderRadius: '20px', fontFamily: 'Montserrat', fontSize: '14px', color: 'var(--app-text, #FFFFFF)' }}><i className="fa-solid fa-circle" style={{ fontSize: 8, color: 'var(--app-tone-eb4040, #EB4040)' }}></i>{t('alerts.unpaidPayroll', 'Unpaid Payroll')}</div>}
                  {lifecycle.alerts?.docsMissing > 0 && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', backgroundColor: '#EBDD404D', border: '1px solid #EBDD4026', borderRadius: '20px', fontFamily: 'Montserrat', fontSize: '14px', color: 'var(--app-text, #FFFFFF)' }}><i className="fa-solid fa-circle" style={{ fontSize: 8, color: 'var(--app-tone-ebdd40, #EBDD40)' }}></i>{t('alerts.missingDocuments', { count: lifecycle.alerts.docsMissing, defaultValue: `${lifecycle.alerts.docsMissing} Document Missing` })}</div>}
                  {lifecycle.alerts?.reviewDue && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', backgroundColor: '#EB8D404D', border: '1px solid #EB8D4026', borderRadius: '20px', fontFamily: 'Montserrat', fontSize: '14px', color: 'var(--app-text, #FFFFFF)' }}><i className="fa-solid fa-circle" style={{ fontSize: 8, color: 'var(--app-tone-eb8d40, #EB8D40)' }}></i>{t('alerts.upcomingReview', 'Upcoming Review')}</div>}
                  {lifecycle.alerts?.leavePending && <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', backgroundColor: 'var(--app-surface, rgba(86,141,237,0.24))', border: "1px solid var(--app-border-strong, rgba(86,141,237,0.28))", borderRadius: '20px', fontFamily: 'Montserrat', fontSize: '14px', color: 'var(--app-text, #FFFFFF)' }}><i className="fa-solid fa-circle" style={{ fontSize: 8, color: '#568ded' }}></i>{t('alerts.reviewLeave', 'Review Leave Request')}</div>}
                </div>
              </>
            )}
            {sectionTitle('fa-solid fa-briefcase', t('drawerSections.employment', 'Employment Details'))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {field(t('drawerFields.startDate', 'Start Date'), profile.startDate || emp.startDate)}
              {field(t('drawerFields.employmentType', 'Employment Type'), employmentTypeLabel(profile.employmentType || 'full_time'))}
              {field(t('drawerFields.manager', 'Manager'), emp.managerName)}
              {field(t('drawerFields.buddy', 'Buddy/Mentor'), profile.buddyMentor)}
              {probation.inProbation && field(t('drawerFields.probationEnd', 'Probation End'), probation.endDate)}
              {location.country && field(t('drawerFields.location', 'Location'), [location.city, location.country].filter(Boolean).join(', '))}
            </div>
            {sectionTitle('fa-solid fa-address-card', t('drawerSections.contact', 'Contact Information'))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {field(t('drawerFields.workEmail', 'Work Email'), profile.workEmail || contact.workEmail, true)}
              {field(t('drawerFields.personalEmail', 'Personal Email'), profile.email || contact.personalEmail || contact.email, true)}
              {field(t('drawerFields.phone', 'Phone'), profile.phone || contact.personalPhone || contact.phone)}
              {field(t('drawerFields.dateOfBirth', 'Date of Birth'), contact.dateOfBirth)}
              {field(t('drawerFields.nationalId', 'National ID'), contact.nationalId)}
            </div>
            {(address.line1 || address.city || address.country) && (
              <>
                {sectionTitle('fa-solid fa-location-dot', t('drawerSections.address', 'Address'))}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  {field(t('drawerFields.street', 'Street Address'), address.line1, true)}
                  {address.line2 && field(t('drawerFields.address2', 'Address Line 2'), address.line2, true)}
                  {field(t('drawerFields.city', 'City'), address.city)}
                  {field(t('drawerFields.region', 'Region/State'), address.region)}
                  {field(t('drawerFields.postal', 'Postal Code'), address.postalCode)}
                  {field(t('drawerFields.country', 'Country'), address.country)}
                </div>
              </>
            )}
            {emergencies && emergencies.length > 0 && (
              <>
                {sectionTitle('fa-solid fa-user-shield', t('drawerSections.emergency', 'Emergency Contacts'))}
                {emergencies.map((c, idx) => (
                  <div key={idx} style={{ backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.08))', border: "1px solid var(--app-border, rgba(86, 141, 237, 0.15))", borderRadius: 12, padding: 20, marginBottom: 16 }}>
                    <div style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, color: 'var(--app-text, #FFFFFF)', marginBottom: 12 }}>
                      {c?.name || '—'}{c?.relationship && <span style={{ opacity: 0.7, fontWeight: 400, fontSize: '14px', marginLeft: 8 }}>({c.relationship})</span>}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                      {field(t('drawerFields.phone', 'Phone'), c?.phone)}
                      {field(t('drawerFields.email', 'Email'), c?.email)}
                    </div>
                  </div>
                ))}
              </>
            )}
            {sectionTitle('fa-solid fa-door-open', t('drawerSections.portal', 'Portal Access'))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {field(t('drawerFields.inviteStatus', 'Invite Status'), t(`drawerFields.status.${portal.invite?.status || 'pending'}`, (portal.invite?.status || 'pending').toUpperCase().replace('_', ' ')))}
              {field(t('drawerFields.lastLogin', 'Last Login'), portal.lastLoginAt ? new Date(portal.lastLoginAt).toLocaleString(locale, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : t('drawerFields.never', 'Never'))}
              {field(t('drawerFields.permissions', 'Permissions'), (portal.permissions || ['employee']).map(p => t(`drawerFields.permissionsMap.${String(p).toLowerCase()}`, String(p).toUpperCase())).join(', '), true)}
              {portal.invite?.sentAt && field(t('drawerFields.inviteSent', 'Invite Sent'), new Date(portal.invite.sentAt).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' }))}
              {portal.invite?.acceptedAt && field(t('drawerFields.inviteAccepted', 'Invite Accepted'), new Date(portal.invite.acceptedAt).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' }))}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <label style={{ fontFamily: 'Montserrat', fontSize: '13px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={portal.permissions?.includes('manager') || false} onChange={toggleManagerRole} style={{ width: '16px', height: '16px', accentColor: '#568ded' }} />
                  {t('drawerFields.managerAccess', 'Manager Access')}
                </label>
              </div>
              <div style={{ gridColumn: '1 / -1', marginTop: 10 }}>
                {!portal.invite?.token ? (
                  <button onClick={async () => {
                    if (!companyUID || !employeeId) return;
                    try {
                      const token = crypto.randomUUID();
                      const updates = {};
                      updates[`companies/${companyUID}/employees/${employeeId}/portal/invite`] = { token, status: 'pending', generatedAt: Date.now() };
                      await update(ref(database), updates);
                    } catch (error) {
                      console.error("Error generating invite:", error);
                      alert(t('drawerFields.generateInviteError', 'Failed to generate invite'));
                    }
                  }} style={{ '--light-control-fill': '#568ded', padding: '8px 16px', backgroundColor: '#568ded', color: '#FFFFFF', border: 'none', borderRadius: '6px', cursor: 'pointer', fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600 }}  data-light-control={"filled"}>
                    {t('drawerFields.generateInvite', 'Generate Portal Invite')}
                  </button>
                ) : portal.invite?.status === 'pending' ? (
                  <div style={{ backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.1))', border: "1px solid var(--app-border-strong, rgba(86, 141, 237, 0.3))", borderRadius: '8px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <span style={{ fontFamily: 'Montserrat', fontSize: '13px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)' }}>{t('drawerFields.inviteLinkLabel', 'Invite Link (Share with Employee):')}</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input readOnly value={`${process.env.REACT_APP_EMPLOYEE_PORTAL_URL || 'http://localhost:3001'}/accept-invite?token=${portal.invite.token}&cid=${companyUID}&eid=${employeeId}`} style={{ flex: 1, backgroundColor: 'rgba(0,0,0,0.2)', border: "1px solid var(--app-border, rgba(255,255,255,0.1))", borderRadius: '4px', color: 'var(--app-text, #fff)', padding: '6px 10px', fontFamily: 'monospace', fontSize: '12px' }} />
                      <button onClick={() => {
                        const url = `${process.env.REACT_APP_EMPLOYEE_PORTAL_URL || 'http://localhost:3001'}/accept-invite?token=${portal.invite.token}&cid=${companyUID}&eid=${employeeId}`;
                        navigator.clipboard.writeText(url);
                        alert(t('drawerFields.copied', 'Link copied to clipboard!'));
                      }} style={{ backgroundColor: 'var(--app-surface, rgba(255,255,255,0.1))', border: 'none', borderRadius: '4px', color: 'var(--app-text, #fff)', cursor: 'pointer', padding: '0 12px' }}  data-light-control={"quiet"}><i className="fa-solid fa-copy"></i></button>
                    </div>
                    <button onClick={async () => {
                      if (window.confirm(t('drawerFields.revokeConfirm', 'Are you sure you want to revoke this invite? The link will no longer work.'))) {
                        try {
                          const updates = {};
                          updates[`companies/${companyUID}/employees/${employeeId}/portal/invite`] = null;
                          await update(ref(database), updates);
                        } catch (e) {
                          console.error(e);
                        }
                      }
                    }} style={{ alignSelf: 'flex-start', background: 'none', border: 'none', color: 'var(--app-tone-eb4040, #EB4040)', fontSize: '12px', cursor: 'pointer', marginTop: '4px', padding: 0 }}  data-light-control={"quiet-danger"}>
                      {t('drawerFields.revokeInvite', 'Revoke Invite')}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          </>
        )}

        {activeTab === 'work' && (
          <>
            {sectionTitle('fa-solid fa-chart-line', t('drawerSections.performance', 'Performance'))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {field(t('drawerFields.currentRating', 'Current Rating'), performance.currentRating ? `${performance.currentRating.toFixed(1)} / 5.0` : t('performance.status.notRated', 'Not Rated'))}
              {field(t('drawerFields.nextReview', 'Next Review Date'), performance.nextReviewDate ? new Date(performance.nextReviewDate).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' }) : '—')}
              {field(t('drawerFields.totalReviews', 'Total Reviews'), performanceReviews.length)}
              {field(t('drawerFields.activeGoals', 'Active Goals'), Object.values(performance.goals || {}).filter(g => g.status !== 'completed').length)}
            </div>
            {sectionTitle('fa-solid fa-user-check', t('employeePage.qualityBeta.title', 'Quality of Hire Beta'))}
            <div style={{ backgroundColor: 'var(--app-surface, rgba(255,255,255,0.03))', border: "1px solid var(--app-border, rgba(86,141,237,0.16))", borderRadius: 8, padding: 20, marginBottom: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 14, marginBottom: 14 }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--app-text, #FFFFFF)', fontSize: 12, fontWeight: 600 }}>
                  {t('employeePage.qualityBeta.betaScore', 'Quality Score')}
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={qualityForm.betaScore ?? ''}
                    onChange={(event) => updateQualityForm('betaScore', event.target.value)}
                    style={{ borderRadius: 6, border: "1px solid var(--app-border, rgba(255,255,255,0.14))", background: 'var(--app-surface, rgba(255,255,255,0.06))', color: 'var(--app-text, #FFFFFF)', padding: '10px 12px' }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--app-text, #FFFFFF)', fontSize: 12, fontWeight: 600 }}>
                  {t('employeePage.qualityBeta.managerRating', 'Manager Rating')}
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.1"
                    value={qualityForm.managerRating ?? ''}
                    onChange={(event) => updateQualityForm('managerRating', event.target.value)}
                    style={{ borderRadius: 6, border: "1px solid var(--app-border, rgba(255,255,255,0.14))", background: 'var(--app-surface, rgba(255,255,255,0.06))', color: 'var(--app-text, #FFFFFF)', padding: '10px 12px' }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--app-text, #FFFFFF)', fontSize: 12, fontWeight: 600 }}>
                  {t('employeePage.qualityBeta.rampStatus', 'Ramp Status')}
                  <select
                    value={qualityForm.rampStatus || 'not_started'}
                    onChange={(event) => updateQualityForm('rampStatus', event.target.value)}
                    style={{ borderRadius: 6, border: "1px solid var(--app-border, rgba(255,255,255,0.14))", background: 'var(--app-background, #031128)', color: 'var(--app-text, #FFFFFF)', padding: '10px 12px' }}
                  >
                    <option value="not_started">{t('employeePage.qualityBeta.ramp.notStarted', 'Not started')}</option>
                    <option value="ramping">{t('employeePage.qualityBeta.ramp.ramping', 'Ramping')}</option>
                    <option value="productive">{t('employeePage.qualityBeta.ramp.productive', 'Productive')}</option>
                    <option value="at_risk">{t('employeePage.qualityBeta.ramp.atRisk', 'At risk')}</option>
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--app-text, #FFFFFF)', fontSize: 12, fontWeight: 600 }}>
                  {t('employeePage.qualityBeta.rampDate', 'Ramp Date')}
                  <input
                    type="date"
                    value={qualityForm.rampDate || ''}
                    onChange={(event) => updateQualityForm('rampDate', event.target.value)}
                    style={{ borderRadius: 6, border: "1px solid var(--app-border, rgba(255,255,255,0.14))", background: 'var(--app-surface, rgba(255,255,255,0.06))', color: 'var(--app-text, #FFFFFF)', padding: '10px 12px' }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--app-text, #FFFFFF)', fontSize: 12, fontWeight: 600 }}>
                  {t('employeePage.qualityBeta.probationStatus', 'Probation Status')}
                  <select
                    value={qualityForm.probationStatus || 'not_applicable'}
                    onChange={(event) => updateQualityForm('probationStatus', event.target.value)}
                    style={{ borderRadius: 6, border: "1px solid var(--app-border, rgba(255,255,255,0.14))", background: 'var(--app-background, #031128)', color: 'var(--app-text, #FFFFFF)', padding: '10px 12px' }}
                  >
                    <option value="not_applicable">{t('employeePage.qualityBeta.probation.notApplicable', 'Not applicable')}</option>
                    <option value="in_progress">{t('employeePage.qualityBeta.probation.inProgress', 'In progress')}</option>
                    <option value="passed">{t('employeePage.qualityBeta.probation.passed', 'Passed')}</option>
                    <option value="extended">{t('employeePage.qualityBeta.probation.extended', 'Extended')}</option>
                    <option value="failed">{t('employeePage.qualityBeta.probation.failed', 'Failed')}</option>
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--app-text, #FFFFFF)', fontSize: 12, fontWeight: 600 }}>
                  {t('employeePage.qualityBeta.probationOutcomeDate', 'Probation Outcome Date')}
                  <input
                    type="date"
                    value={qualityForm.probationOutcomeDate || ''}
                    onChange={(event) => updateQualityForm('probationOutcomeDate', event.target.value)}
                    style={{ borderRadius: 6, border: "1px solid var(--app-border, rgba(255,255,255,0.14))", background: 'var(--app-surface, rgba(255,255,255,0.06))', color: 'var(--app-text, #FFFFFF)', padding: '10px 12px' }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--app-text, #FFFFFF)', fontSize: 12, fontWeight: 600 }}>
                  {t('employeePage.qualityBeta.lifecycleStatus', 'Lifecycle Status')}
                  <select
                    value={qualityForm.lifecycleStatus || 'active'}
                    onChange={(event) => updateQualityForm('lifecycleStatus', event.target.value)}
                    style={{ borderRadius: 6, border: "1px solid var(--app-border, rgba(255,255,255,0.14))", background: 'var(--app-background, #031128)', color: 'var(--app-text, #FFFFFF)', padding: '10px 12px' }}
                  >
                    <option value="active">{t('status.active', 'Active')}</option>
                    <option value="onboarding">{t('status.onboarding', 'Onboarding')}</option>
                    <option value="on_leave">{t('status.onLeave', 'On Leave / PTO')}</option>
                    <option value="terminated">{t('status.terminated', 'Terminated')}</option>
                    <option value="offboarded">{t('status.offboarded', 'Offboarded')}</option>
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--app-text, #FFFFFF)', fontSize: 12, fontWeight: 600 }}>
                  {t('employeePage.qualityBeta.terminationDate', 'Termination Date')}
                  <input
                    type="date"
                    value={qualityForm.terminationDate || ''}
                    onChange={(event) => updateQualityForm('terminationDate', event.target.value)}
                    style={{ borderRadius: 6, border: "1px solid var(--app-border, rgba(255,255,255,0.14))", background: 'var(--app-surface, rgba(255,255,255,0.06))', color: 'var(--app-text, #FFFFFF)', padding: '10px 12px' }}
                  />
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--app-text, #FFFFFF)', fontSize: 12, fontWeight: 600 }}>
                  {t('employeePage.qualityBeta.terminationType', 'Termination Type')}
                  <select
                    value={qualityForm.terminationType || 'unknown'}
                    onChange={(event) => updateQualityForm('terminationType', event.target.value)}
                    style={{ borderRadius: 6, border: "1px solid var(--app-border, rgba(255,255,255,0.14))", background: 'var(--app-background, #031128)', color: 'var(--app-text, #FFFFFF)', padding: '10px 12px' }}
                  >
                    <option value="unknown">{t('employeePage.qualityBeta.termination.unknown', 'Unknown')}</option>
                    <option value="voluntary">{t('employeePage.qualityBeta.termination.voluntary', 'Voluntary')}</option>
                    <option value="involuntary">{t('employeePage.qualityBeta.termination.involuntary', 'Involuntary')}</option>
                  </select>
                </label>
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--app-text, #FFFFFF)', fontSize: 12, fontWeight: 600, marginBottom: 14 }}>
                {t('employeePage.qualityBeta.terminationReason', 'Termination Reason')}
                <input
                  value={qualityForm.terminationReason || ''}
                  onChange={(event) => updateQualityForm('terminationReason', event.target.value)}
                  style={{ borderRadius: 6, border: "1px solid var(--app-border, rgba(255,255,255,0.14))", background: 'var(--app-surface, rgba(255,255,255,0.06))', color: 'var(--app-text, #FFFFFF)', padding: '10px 12px' }}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, color: 'var(--app-text, #FFFFFF)', fontSize: 12, fontWeight: 600 }}>
                {t('employeePage.qualityBeta.notes', 'Quality Notes')}
                <textarea
                  value={qualityForm.notes || ''}
                  onChange={(event) => updateQualityForm('notes', event.target.value)}
                  rows={3}
                  style={{ borderRadius: 6, border: "1px solid var(--app-border, rgba(255,255,255,0.14))", background: 'var(--app-surface, rgba(255,255,255,0.06))', color: 'var(--app-text, #FFFFFF)', padding: '10px 12px', resize: 'vertical' }}
                />
              </label>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <button
                  type="button"
                  onClick={handleSaveQualityBeta}
                  disabled={qualitySaving}
                  style={{ '--light-control-fill': qualitySaving ? 'var(--app-surface, rgba(255,255,255,0.12))' : '#568ded', display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, border: 'none', backgroundColor: qualitySaving ? 'var(--app-surface, rgba(255,255,255,0.12))' : '#568ded', color: qualitySaving ? 'var(--app-text, #FFFFFF)' : "#FFFFFF", fontWeight: "var(--app-weight-strong, 700)", cursor: qualitySaving ? 'not-allowed' : 'pointer' }}
                  data-light-control={qualitySaving ? "quiet" : "filled"}>
                  <i className={`fa-solid ${qualitySaving ? 'fa-spinner fa-spin' : 'fa-floppy-disk'}`} />
                  {qualitySaving ? t('employeePage.qualityBeta.saving', 'Saving...') : t('employeePage.qualityBeta.save', 'Save Quality Beta')}
                </button>
              </div>
            </div>
            {performanceReviews.length > 0 && <>
              {sectionTitle('fa-solid fa-star', t('drawerSections.performanceReviews', 'Performance Reviews'))}
              {performanceReviews.slice(0, 3).map(([id, review]) => (
                <div key={id} style={{ backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.08))', border: "1px solid var(--app-border, rgba(86, 141, 237, 0.15))", borderRadius: 12, padding: 20, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, color: 'var(--app-text, #FFFFFF)' }}>{review.period || review.title || uiText("Performance Review")}</div>
                    {review.rating && <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', backgroundColor: review.rating >= 4 ? '#40EB6E4D' : review.rating >= 3 ? '#EBDD404D' : '#EB40404D', border: `1px solid ${review.rating >= 4 ? '#40EB6E26' : review.rating >= 3 ? '#EBDD4026' : '#EB404026'}`, borderRadius: '16px' }}><i className="fa-solid fa-star" style={{ fontSize: 12, color: 'var(--app-text, #FFFFFF)' }}></i><span style={{ fontFamily: 'Montserrat', fontSize: '14px', fontWeight: 600, color: 'var(--app-text, #FFFFFF)' }}>{review.rating.toFixed(1)}</span></div>}
                  </div>
                  <div style={{ fontFamily: 'Montserrat', fontSize: '13px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)', marginBottom: 8 }}>
                    {review.reviewDate && new Date(review.reviewDate).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })}{review.reviewedBy && ` • ${t('drawerFields.by', { name: review.reviewedBy, defaultValue: `by ${review.reviewedBy}` })}`}
                  </div>
                  {review.summary && <div style={{ fontFamily: 'Montserrat', fontSize: '14px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.8)', lineHeight: 1.5 }}>{review.summary}</div>}
                </div>
              ))}
            </>}
            {performanceGoals.length > 0 && <>
              {sectionTitle('fa-solid fa-bullseye', t('drawerSections.goals', 'Goals & Objectives'))}
              {performanceGoals.map(([id, goal]) => (
                <div key={id} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: 16, backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.08))', border: "1px solid var(--app-border, rgba(86, 141, 237, 0.15))", borderRadius: 12, marginBottom: 12 }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${goal.status === 'completed' ? '#40EB6E' : goal.status === 'in_progress' ? '#EBDD40' : '#EB4040'}`, backgroundColor: goal.status === 'completed' ? '#40EB6E' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {goal.status === 'completed' && <i className="fa-solid fa-check" style={{ fontSize: 10, color: 'var(--app-text, #FFFFFF)' }}></i>}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'Montserrat', fontSize: '15px', fontWeight: 600, color: 'var(--app-text, #FFFFFF)', marginBottom: 4 }}>{goal.title || goal.description || uiText("Goal")}</div>
                    {goal.dueDate && <div style={{ fontFamily: 'Montserrat', fontSize: '13px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)' }}>{t('drawerFields.due', 'Due:')} {new Date(goal.dueDate).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' })}</div>}
                  </div>
                </div>
              ))}
            </>}
            {sectionTitle('fa-solid fa-road', t('employeePage.learningPathways.title', 'Learning Pathways'))}
            <div style={{ backgroundColor: 'var(--app-surface, rgba(255,255,255,0.03))', border: "1px solid var(--app-border, rgba(86,141,237,0.16))", borderRadius: 8, padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 16 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{ fontSize: 17, fontWeight: "var(--app-weight-strong, 700)", color: 'var(--app-text, #FFFFFF)' }}>
                      {learningPathway.status === 'completed'
                        ? t('employeePage.learningPathways.readyTitle', 'Learning pathway ready')
                        : t('employeePage.learningPathways.emptyTitle', 'Generate a learning pathway')}
                    </div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 999, backgroundColor: pathwayStatus === 'completed' ? 'rgba(64,235,110,0.16)' : pathwayStatus === 'failed' ? 'rgba(235,64,64,0.16)' : 'var(--app-surface, rgba(86,141,237,0.12))', border: `1px solid ${pathwayStatus === 'completed' ? 'rgba(64,235,110,0.24)' : pathwayStatus === 'failed' ? 'rgba(235,64,64,0.24)' : 'var(--app-border, rgba(86,141,237,0.22))'}`, color: 'var(--app-text, #FFFFFF)', fontSize: 12, fontWeight: 600 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: pathwayStatus === 'completed' ? '#40EB6E' : pathwayStatus === 'failed' ? '#EB4040' : '#568ded' }} />
                      {t(`employeePage.learningPathways.statusValues.${pathwayStatus}`, pathwayStatus)}
                    </div>
                  </div>
                  <div style={{ fontSize: 14, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.72)', lineHeight: 1.6 }}>
                    {hasUploadedCv
                      ? t('employeePage.learningPathways.cvReady', 'Uses the employee resume/CV on file plus role, performance, and mobility data.')
                      : t('employeePage.learningPathways.noCv', 'Upload a resume/CV in Files before generating a plan.')}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                  <button
                    onClick={handleGenerateLearningPathway}
                    disabled={!hasUploadedCv || pathwayGenerating}
                    style={{ '--light-control-fill': (!hasUploadedCv || pathwayGenerating) ? 'var(--app-surface, rgba(255,255,255,0.12))' : '#568ded', display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 13px', borderRadius: 6, border: 'none', backgroundColor: (!hasUploadedCv || pathwayGenerating) ? 'var(--app-surface, rgba(255,255,255,0.12))' : '#568ded', color: !hasUploadedCv || pathwayGenerating ? 'var(--app-text, #FFFFFF)' : "#FFFFFF", fontSize: 13, fontWeight: "var(--app-weight-strong, 700)", cursor: (!hasUploadedCv || pathwayGenerating) ? 'not-allowed' : 'pointer' }}
                    data-light-control={!hasUploadedCv || pathwayGenerating ? "quiet" : "filled"}>
                    <i className={`fa-solid ${pathwayGenerating ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'}`}></i>
                    {pathwayGenerating
                      ? t('employeePage.learningPathways.generating', 'Generating...')
                      : learningPathway.status === 'completed'
                        ? t('employeePage.learningPathways.regenerate', 'Regenerate')
                        : t('employeePage.learningPathways.generate', 'Generate')}
                  </button>
                  <button
                    onClick={openLearningPathway}
                    disabled={learningPathway.status !== 'completed'}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 13px', borderRadius: 6, border: "1px solid var(--app-border, rgba(255,255,255,0.14))", backgroundColor: learningPathway.status === 'completed' ? 'var(--app-surface, rgba(255,255,255,0.06))' : 'var(--app-surface, rgba(255,255,255,0.03))', color: 'var(--app-text, #FFFFFF)', fontSize: 13, fontWeight: "var(--app-weight-strong, 700)", cursor: learningPathway.status === 'completed' ? 'pointer' : 'not-allowed', opacity: learningPathway.status === 'completed' ? 1 : 0.5 }}
                    data-light-control={learningPathway.status === 'completed' ? "quiet" : "quiet"}>
                    <i className="fa-solid fa-up-right-from-square"></i>
                    {t('employeePage.learningPathways.openFullPlan', 'Open full plan')}
                  </button>
                </div>
              </div>

              {(pathwayError || learningPathway.error) && (
                <div style={{ marginBottom: 14, color: 'var(--app-tone-ff9a9a, #ff9a9a)', fontSize: 13, lineHeight: 1.5 }}>
                  {pathwayError || learningPathway.error}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12, marginBottom: learningPathway.status === 'completed' ? 16 : 0 }}>
                <div style={{ backgroundColor: 'rgba(0,0,0,0.16)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", borderRadius: 6, padding: 12 }}>
                  <div style={{ fontSize: 11, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.52)', marginBottom: 5 }}>{t('employeePage.learningPathways.targetRole', 'Target role')}</div>
                  <div style={{ fontSize: 14, fontWeight: "var(--app-weight-strong, 700)", color: 'var(--app-text, #FFFFFF)' }}>{pathwayTarget.targetRole || mobility.targetRole || profile.roleTitle || emp.role || '—'}</div>
                </div>
                <div style={{ backgroundColor: 'rgba(0,0,0,0.16)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", borderRadius: 6, padding: 12 }}>
                  <div style={{ fontSize: 11, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.52)', marginBottom: 5 }}>{t('employeePage.learningPathways.steps', 'Steps')}</div>
                  <div style={{ fontSize: 14, fontWeight: "var(--app-weight-strong, 700)", color: 'var(--app-text, #FFFFFF)' }}>{(learningPathway.steps || []).length}</div>
                </div>
                <div style={{ backgroundColor: 'rgba(0,0,0,0.16)', border: "1px solid var(--app-border, rgba(255,255,255,0.08))", borderRadius: 6, padding: 12 }}>
                  <div style={{ fontSize: 11, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.52)', marginBottom: 5 }}>{t('employeePage.learningPathways.selectedCourses', 'Selected courses')}</div>
                  <div style={{ fontSize: 14, fontWeight: "var(--app-weight-strong, 700)", color: 'var(--app-text, #FFFFFF)' }}>{selectedCourseCount}</div>
                </div>
              </div>

              {learningPathway.status === 'completed' && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
                  {(learningPathway.steps || []).slice(0, 3).map((step, index) => (
                    <button
                      key={step.id || index}
                      onClick={openLearningPathway}
                      style={{ textAlign: 'left', backgroundColor: 'var(--app-surface, rgba(86,141,237,0.08))', border: "1px solid var(--app-border, rgba(86,141,237,0.16))", borderRadius: 6, padding: 14, color: 'var(--app-text, #FFFFFF)', cursor: 'pointer', minHeight: 92 }}
                      data-light-control={"quiet"}>
                      <div style={{ fontSize: 11, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.52)', marginBottom: 7 }}>{t('employeePage.learningPathways.stepLabel', { index: index + 1, defaultValue: `Step ${index + 1}` })}</div>
                      <div style={{ fontSize: 14, fontWeight: "var(--app-weight-strong, 700)", marginBottom: 6, lineHeight: 1.35 }}>{step.title}</div>
                      <div style={{ fontSize: 12, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.62)', lineHeight: 1.45 }}>{step.profileTag || step.type}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {sectionTitle('fa-solid fa-arrow-right-arrow-left', t('employeePage.mobility.title', 'Internal Mobility'))}
            <div style={{ backgroundColor: 'var(--app-surface, rgba(255,255,255,0.03))', border: "1px solid var(--app-border, rgba(86,141,237,0.16))", borderRadius: 16, padding: 20 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 20 }}>
                <div>
                  <div style={{ fontSize: 12, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.55)', marginBottom: 8 }}>{t('employeePage.mobility.currentTrack', 'Current track')}</div>
                  <div style={{ fontSize: 18, fontWeight: "var(--app-weight-strong, 700)", color: 'var(--app-text, #FFFFFF)', marginBottom: 8 }}>{profile.roleTitle || emp.role || '—'}</div>
                  <div style={{ fontSize: 14, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.68)', marginBottom: 18 }}>{departmentLabel || '—'}</div>
                  <div style={{ fontSize: 12, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.55)', marginBottom: 8 }}>{t('employeePage.mobility.recommendedMove', 'Recommended move')}</div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--app-text, #FFFFFF)', marginBottom: 6 }}>
                    {mobility.targetRole || t('employeePage.mobility.noPlan', 'No move planned yet')}
                  </div>
                  <div style={{ fontSize: 14, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.68)' }}>
                    {mobility.targetDepartment || t('employeePage.mobility.noPlanDesc', 'Use this space to track transfers, promotions, and next-role planning.')}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 16 }}>
                  <div>
                    <div style={{ fontSize: 12, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.55)', marginBottom: 8 }}>{t('employeePage.mobility.readiness', 'Readiness')}</div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 12px', borderRadius: 999, backgroundColor: 'var(--app-surface, rgba(86,141,237,0.12))', border: "1px solid var(--app-border, rgba(86,141,237,0.22))", color: 'var(--app-text, #FFFFFF)', fontSize: 13, fontWeight: 600 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: mobilityReadiness === 'ready_now' ? '#40EB6E' : mobilityReadiness === 'developing' ? '#EBDD40' : '#568ded' }} />
                      {t(`employeePage.mobility.readinessValues.${mobilityReadiness}`, mobilityReadiness)}
                    </div>
                  </div>
                  <div style={{ fontSize: 13, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.58)', lineHeight: 1.6 }}>
                    {mobility.effectiveDate
                      ? `${t('employeePage.mobility.effectiveDate', 'Effective date')}: ${mobility.effectiveDate}`
                      : t('employeePage.mobility.helper', 'Capture the next internal move here so promotions and lateral transfers are visible alongside performance data.')}
                  </div>
                  <div>
                    <ActionButton
                      icon="fa-arrow-right-arrow-left"
                      label={t('employeePage.mobility.manage', 'Manage Internal Move')}
                      onClick={() => setActiveModal('internal-mobility')}
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'pay' && (
          <>
            {sectionTitle('fa-solid fa-plane-departure', t('drawerSections.pto', 'PTO / Time Off'))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {field(t('drawerFields.annualRemaining', 'Annual Leave Remaining'), t('pto.requestDays', { count: pto.balances?.annual?.remaining || 0, defaultValue: '{{count}} days left' }))}
              {field(t('drawerFields.annualUsed', 'Annual Leave Used'), t('pto.usedEntitled', { used: pto.balances?.annual?.used || 0, entitled: pto.balances?.annual?.entitled || 0, defaultValue: '{{used}} used / {{entitled}} entitled' }))}
              {field(t('drawerFields.sickRemaining', 'Sick Leave Remaining'), t('pto.requestDays', { count: pto.balances?.sick?.remaining || 0, defaultValue: '{{count}} days left' }))}
              {field(t('drawerFields.unpaidUsed', 'Unpaid Leave Used'), t('pto.requestDays', { count: pto.balances?.unpaid?.used || 0, defaultValue: '{{count}} days' }))}
              {pto.nextReturnDate && field(t('drawerFields.nextReturn', 'Next Return Date'), new Date(pto.nextReturnDate).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' }), true)}
            </div>
            {Object.keys(pto.requests || {}).length > 0 && <>
              {sectionTitle('fa-solid fa-calendar-check', t('drawerSections.ptoRequests', 'PTO Requests'))}
              {Object.entries(pto.requests || {}).map(([id, req]) => (
                <div key={id} style={{ backgroundColor: 'var(--app-surface, rgba(86, 141, 237, 0.08))', border: "1px solid var(--app-border, rgba(86, 141, 237, 0.15))", borderRadius: 12, padding: 20, marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div>
                      <div style={{ fontFamily: 'Montserrat', fontSize: '16px', fontWeight: 600, color: 'var(--app-text, #FFFFFF)', marginBottom: 4 }}>{t(`pto.types.${req.type || 'vacation'}`, req.type || 'vacation')} {t('drawerFields.leave', 'Leave')}</div>
                      <div style={{ fontFamily: 'Montserrat', fontSize: '14px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)' }}>{t('pto.requestDays', { count: req.days || 0, defaultValue: `${req.days || 0} days` })} • {req.startDate} {t('drawerFields.to', 'to')} {req.endDate}</div>
                    </div>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', backgroundColor: req.status === 'pending' ? '#EBDD404D' : req.status === 'approved' ? '#40EB6E4D' : '#EB40404D', border: `1px solid ${req.status === 'pending' ? '#EBDD4026' : req.status === 'approved' ? '#40EB6E26' : '#EB404026'}`, borderRadius: '16px', fontFamily: 'Montserrat', fontSize: '13px', fontWeight: 600, color: 'var(--app-text, #FFFFFF)', textTransform: 'capitalize' }}>{t(`drawerFields.status.${req.status || 'pending'}`, (req.status || 'pending').toUpperCase())}</div>
                  </div>
                  {req.comment && <div style={{ fontFamily: 'Montserrat', fontSize: '14px', color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.8)', fontStyle: 'italic' }}>"{req.comment}"</div>}
                </div>
              ))}
            </>}
            {sectionTitle('fa-solid fa-money-bill-wave', t('drawerSections.comp', 'Compensation'))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {field(t('comp.columns.base', 'Base Salary'), compensation.base ? `$${compensation.base.toLocaleString()}` : '—')}
              {field(t('comp.columns.schedule', 'Pay Schedule'), compensation.paySchedule || '—')}
              {field(t('comp.columns.status', 'Payroll Status'), isPaidThisMonth ? t('comp.status.paid', 'Paid') : t('comp.status.pending', 'Pending'))}
              {field(t('employeePage.cards.monthlyPay', 'Monthly pay'), `$${monthlyTotal.toLocaleString()}`)}
            </div>
          </>
        )}

        {activeTab === 'documents' && (
          <>
            {sectionTitle('fa-solid fa-folder-open', t('drawerSections.documents', 'Documents'))}
            <div style={{ marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20, marginBottom: 20 }}>
                {field(t('drawerFields.docsOnFile', 'Documents on File'), t('documents.filesCount', { count: Object.keys(documents.files || {}).length, defaultValue: '{{count}} file(s)' }))}
                {field(t('drawerFields.requiredDocs', 'Required Documents'), t('documents.requiredCount', { count: (documents.required || []).length, defaultValue: '{{count}} required' }))}
                {documents.missingCount > 0 && field(t('drawerFields.missingDocs', 'Missing Documents'), t('alerts.missingDocuments', { count: documents.missingCount, defaultValue: 'Missing {{count}} Document' }), true)}
              </div>
              <DocumentManager
                employeeId={employeeId}
                companyUID={companyUID}
                documents={documents}
                onRefresh={() => { fetchActivities(); }}
              />
            </div>
            {sectionTitle('fa-solid fa-info-circle', t('drawerSections.system', 'System Information'))}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {field(t('drawerFields.created', 'Created'), meta.createdAt ? new Date(meta.createdAt).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' }) : '—')}
              {field(t('drawerFields.updated', 'Last Updated'), meta.updatedAt ? new Date(meta.updatedAt).toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' }) : '—')}
              {field(t('drawerFields.source', 'Source'), t(`drawerFields.sourceValues.${String(meta.source || 'manual').toLowerCase()}`, (meta.source || 'manual').replace('_', ' ')))}
            </div>
          </>
        )}

        {activeTab === 'activity' && (
          <>
            {sectionTitle('fa-solid fa-clock-rotate-left', t('drawerSections.timeline', 'Activity Timeline'))}
            <ActivityTimeline activities={activities} loading={loadingActivities} />
            {sectionTitle('fa-solid fa-sticky-note', t('drawerSections.notes', 'Internal Notes'))}
            <NotesSection
              ref={notesSectionRef}
              employeeId={employeeId}
              companyUID={companyUID}
              notes={notes}
              currentUser={currentUser}
              onAddNote={fetchNotes}
              loading={loadingNotes}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 20 }}>
              <ActionButton icon="fa-calendar-plus" label={t('drawerQuickActions.scheduleReview', 'Schedule Review')} onClick={() => handleQuickAction('schedule-review')} />
              <ActionButton icon="fa-clipboard-check" label={t('drawerQuickActions.reviewRequests', 'Review Requests')} onClick={() => handleQuickAction('review-requests')} count={pendingRequestsCount} />
              <ActionButton icon="fa-dollar-sign" label={t('drawerQuickActions.editComp', 'Edit Compensation')} onClick={() => handleQuickAction('edit-comp')} />
              <ActionButton icon="fa-sack-dollar" label={t('drawerQuickActions.addBonus', 'Add Bonus')} onClick={() => handleQuickAction('add-bonus')} />
              <ActionButton icon="fa-user-slash" label={t('drawerQuickActions.offboard', 'Offboard')} onClick={() => handleQuickAction('offboard')} />
            </div>
          </>
        )}

        <div style={{ height: 40 }} />
        </div>
      </div>

      {/* Modals */}
      {activeModal === 'email' && (
        <EmailComposer
          employeeId={employeeId}
          companyUID={companyUID}
          employeeName={profile.name || emp.name}
          employeeEmail={profile.workEmail || contact.workEmail || contact.email}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'schedule-review' && (
        <ScheduleReviewModal
          employeeId={employeeId}
          companyUID={companyUID}
          employeeName={profile.name || emp.name}
          employeeEmail={profile.workEmail || contact.workEmail || contact.email}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'approve-pto' && (
        <ApprovePTOModal
          employeeId={employeeId}
          companyUID={companyUID}
          employeeName={profile.name || emp.name}
          ptoRequests={pto.requests || {}}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'edit-comp' && (
        <EditCompensationModal
          employeeId={employeeId}
          companyUID={companyUID}
          employeeName={profile.name || emp.name}
          currentCompensation={compensation}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'upload-doc' && (
        <UploadDocumentModal
          employeeId={employeeId}
          companyUID={companyUID}
          employeeName={profile.name || emp.name}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'add-bonus' && (
        <AddBonusModal
          employeeId={employeeId}
          companyUID={companyUID}
          employeeName={profile.name || emp.name}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'offboard' && (
        <OffboardModal
          employeeId={employeeId}
          companyUID={companyUID}
          employeeName={profile.name || emp.name}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'review-requests' && (
        <ReviewRequestsModal
          employeeId={employeeId}
          companyUID={companyUID}
          employeeName={profile.name || emp.name}
          requests={pendingHRRequests}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
        />
      )}

      {activeModal === 'internal-mobility' && (
        <InternalMobilityModal
          employee={emp}
          profile={profile}
          mobility={mobility}
          readiness={mobilityReadiness}
          saving={mobilitySaving}
          onClose={handleModalClose}
          onSave={handleSaveMobility}
          t={t}
        />
      )}
    </>
  );
};

const InternalMobilityModal = ({ employee, profile, mobility, readiness, saving, onClose, onSave, t }) => {
  const [form, setForm] = useState({
    targetRole: mobility.targetRole || profile.roleTitle || employee.role || '',
    targetDepartment: mobility.targetDepartment || profile.department || employee.department || '',
    readiness: mobility.readiness || readiness || 'developing',
    effectiveDate: mobility.effectiveDate || '',
    notes: mobility.notes || ''
  });

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(720px, 100%)',
          backgroundColor: 'var(--app-surface, #081a3b)',
          border: "1px solid var(--app-border, rgba(86,141,237,0.2))",
          zIndex: 3001
        }}
       >
        <div className="modal-header">
          <div>
            <h2 style={{ color: 'var(--app-text, #FFFFFF)', marginBottom: 6 }}>
              {t('employeePage.mobility.title', 'Internal Mobility')}
            </h2>
            <div style={{ fontSize: 14, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.6)' }}>
              {t('employeePage.mobility.modalSubtitle', 'Track promotions, lateral moves, and future-role planning from the employee record.')}
            </div>
          </div>
          <button className="close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-page-container" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 8, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)', fontSize: 13 }}>{t('employeePage.mobility.targetRole', 'Target role')}</label>
            <input value={form.targetRole} onChange={(e) => setField('targetRole', e.target.value)} style={modalInputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)', fontSize: 13 }}>{t('employeePage.mobility.targetDepartment', 'Target department')}</label>
            <input value={form.targetDepartment} onChange={(e) => setField('targetDepartment', e.target.value)} style={modalInputStyle} />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)', fontSize: 13 }}>{t('employeePage.mobility.readiness', 'Readiness')}</label>
            <select value={form.readiness} onChange={(e) => setField('readiness', e.target.value)} style={modalInputStyle}>
              <option value="ready_now">{t('employeePage.mobility.readinessValues.ready_now', 'Ready now')}</option>
              <option value="developing">{t('employeePage.mobility.readinessValues.developing', 'Developing')}</option>
              <option value="not_ready">{t('employeePage.mobility.readinessValues.not_ready', 'Needs development')}</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)', fontSize: 13 }}>{t('employeePage.mobility.effectiveDate', 'Effective date')}</label>
            <input type="date" value={form.effectiveDate} onChange={(e) => setField('effectiveDate', e.target.value)} style={modalInputStyle} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', marginBottom: 8, color: 'rgba(var(--app-ink-rgb, 255, 255, 255), 0.7)', fontSize: 13 }}>{t('employeePage.mobility.notes', 'Notes')}</label>
            <textarea value={form.notes} onChange={(e) => setField('notes', e.target.value)} rows={5} style={{ ...modalInputStyle, resize: 'vertical' }} />
          </div>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn-secondary" onClick={onClose}>{t('common:buttons.cancel', 'Cancel')}</button>
          <button type="button" className="btn-primary" onClick={() => onSave(form)} disabled={saving}>
            {saving ? t('common:status.saving', 'Saving...') : t('employeePage.mobility.save', 'Save Move')}
          </button>
        </div>
      </div>
    </div>
  );
};

const modalInputStyle = {
  width: '100%',
  backgroundColor: 'var(--app-surface, rgba(255,255,255,0.04))',
  border: "1px solid var(--app-border, rgba(86,141,237,0.18))",
  borderRadius: 10,
  color: 'var(--app-text, #FFFFFF)',
  padding: '12px 14px',
  boxSizing: 'border-box',
  fontSize: 14
};

export default EmployeePage;
